import {
  BR_STATUS,
  filterLinkedBrListItems,
  groupBrListItems,
  isBrPendingCode,
  isBrVerifiedItem,
  resolveBrDisplayStatus,
  sortBrListItemsByLastUpdated,
} from './brDocument';
import { BR_REQUEST_KIND, isBrNameChangeRequestKind } from '../../../constants/brRequestKind';

/** 更名申请行（合成卡 / 待审标 / requestKind），不含结果照 NAME_CHANGE_RESULT */
export function isBrNameChangeRequestRow(item) {
  if (!item) {
    return false;
  }
  return !!(
    item.isNameChangeRequestCard
    || item.IsNameChangeRequestCard
    || item.isNameChangePending
    || item.IsNameChangePending
    || isBrNameChangeRequestKind(item)
  );
}

function pick(obj, camel, pascal, fallback = '') {
  const value = obj?.[camel] ?? obj?.[pascal];
  return value == null ? fallback : value;
}

function pickTrim(obj, camel, pascal) {
  return String(pick(obj, camel, pascal, '')).trim();
}

function approvalIdOf(item) {
  return String(pick(item, 'approvalId', 'ApprovalId', ''));
}

/** 更名申请卡 approvalId 集合，用于隐藏列表里重复的 request 行 */
export function buildNameChangeRequestApprovalIdSet(items) {
  return new Set(
    (items || []).map((row) => approvalIdOf(row)).filter(Boolean),
  );
}

/** 去掉已由更名申请卡展示的重复 request 行 */
export function withoutDuplicateNameChangeRequestRows(items, approvalIdSet) {
  if (!approvalIdSet || !approvalIdSet.size) {
    return items || [];
  }
  return (items || []).filter((row) => !approvalIdSet.has(approvalIdOf(row)));
}

function brIdOf(item) {
  return String(pick(item, 'businessRegistrationId', 'BusinessRegistrationId', ''));
}

function statusCodeOf(item) {
  return String(pick(item, 'statusCode', 'StatusCode', '')).toUpperCase();
}

function normalizePendingList(pending) {
  return Array.isArray(pending) ? pending : (pending ? [pending] : []);
}

function idsEqual(left, right) {
  return left != null && String(left) === String(right);
}

/** 可作更名依据的已生效执照（不含合成更名卡；已失效 / 未选用更名单不入选） */
export function getApprovedBrForNameChangeOptions(items) {
  return sortBrListItemsByLastUpdated(
    (Array.isArray(items) ? items : []).filter((item) => {
      if (item?.isNameChangePending || item?.IsNameChangePending) {
        return false;
      }
      const brId = Number(pick(item, 'businessRegistrationId', 'BusinessRegistrationId', 0));
      return Number.isFinite(brId) && brId > 0
        && isBrVerifiedItem(item);
    }),
  );
}

/**
 * 標記「正作為更名來源、且更名仍在人工審批中」的真實 BR 卡。
 * 用於在 AAA（來源已生效照）上展示 Figma 信息條；更名申請卡本身不標記。
 * 審批通過 / 駁回後更名不再是 P/PA → 不標記 → 藍條消失。
 * 注意：上一輪更名產生的「結果照」仍可作為下一輪更名來源，必須允許標記。
 */
export function annotateBrItemsWithPendingNameChangeSource(items, nameChanges) {
  const list = Array.isArray(items) ? items : [];
  const pendingSources = normalizePendingList(nameChanges)
    .filter((row) => isBrPendingCode(statusCodeOf(row)))
    .map((row) => {
      const sourceApprovalId = pick(row, 'sourceApprovalId', 'SourceApprovalId', null);
      // GetAdminPending 把來源主檔寫在 BusinessRegistrationId；Content 另有 sourceBusinessRegistrationId
      const sourceBusinessRegistrationId = pick(
        row,
        'sourceBusinessRegistrationId',
        'SourceBusinessRegistrationId',
        null,
      ) || (
        isBrNameChangeRequestRow(row)
          ? pick(row, 'businessRegistrationId', 'BusinessRegistrationId', null)
          : null
      );
      return { sourceApprovalId, sourceBusinessRegistrationId };
    })
    .filter((x) => x.sourceApprovalId != null || x.sourceBusinessRegistrationId != null);

  if (!pendingSources.length) {
    return list.map((item) => ({
      ...item,
      isSourceOfPendingNameChange: false,
    }));
  }

  return list.map((item) => {
    /* 僅排除「更名申請合成卡」；結果照 / 普通 BR 都可作來源並顯示藍條 */
    if (item?.isNameChangeRequestCard || item?.IsNameChangeRequestCard) {
      return {
        ...item,
        isSourceOfPendingNameChange: false,
      };
    }
    /* 來源已失效 / 已駁回：不應再掛「審批中」提示 */
    const itemStatus = resolveBrDisplayStatus(item);
    if (
      itemStatus === BR_STATUS.INVALID
      || itemStatus === BR_STATUS.REJECTED
      || itemStatus === BR_STATUS.CANCELLED
    ) {
      return {
        ...item,
        isSourceOfPendingNameChange: false,
      };
    }
    const isSource = pendingSources.some((src) => (
      (src.sourceApprovalId != null && idsEqual(approvalIdOf(item), src.sourceApprovalId))
      || (src.sourceBusinessRegistrationId != null
        && idsEqual(brIdOf(item), src.sourceBusinessRegistrationId))
    ));
    return {
      ...item,
      isSourceOfPendingNameChange: isSource,
    };
  });
}

function resolveSourceBrItem(nameChange, items) {
  const list = Array.isArray(items) ? items : [];
  const sourceApprovalId = pick(nameChange, 'sourceApprovalId', 'SourceApprovalId', null);
  if (sourceApprovalId != null) {
    const found = list.find((item) => idsEqual(approvalIdOf(item), sourceApprovalId));
    if (found) {
      return found;
    }
  }
  const sourceBrId = pick(
    nameChange,
    'sourceBusinessRegistrationId',
    'SourceBusinessRegistrationId',
    null,
  );
  if (sourceBrId == null) {
    return null;
  }
  return list.find((item) => idsEqual(brIdOf(item), sourceBrId)) || null;
}

function buildNameChangeCardCommon(pending, overrides) {
  const pendingId = pick(pending, 'approvalId', 'ApprovalId', null);
  const submitted = pick(pending, 'submittedDate', 'SubmittedDate', null);
  const displayNameEn = pickTrim(pending, 'displayNameEn', 'DisplayNameEn');
  const displayNameZh = pickTrim(pending, 'displayNameZh', 'DisplayNameZh');
  // 更名主档自身 ID。禁止用来源执照 ID，否则 SoftDelete 会打到已通过源档。
  const ownBrId = pick(pending, 'businessRegistrationId', 'BusinessRegistrationId', null)
    ?? pick(pending, 'resultBusinessRegistrationId', 'ResultBusinessRegistrationId', null);
  return {
    approvalId: pendingId,
    businessRegistrationId: ownBrId,
    displayNameEn,
    displayNameZh,
    submittedDate: submitted,
    isNameChangeRequestCard: true,
    requestKind: BR_REQUEST_KIND.NAME_CHANGE,
    sourceApprovalId: pick(pending, 'sourceApprovalId', 'SourceApprovalId', null),
    sourceBusinessRegistrationId: pick(
      pending,
      'sourceBusinessRegistrationId',
      'SourceBusinessRegistrationId',
      null,
    ),
    ...overrides,
  };
}

function mergeNameChangeCard(pending, items, overrides) {
  const source = resolveSourceBrItem(pending, items);
  const common = buildNameChangeCardCommon(pending, overrides);
  if (source) {
    // common 必须后铺，覆盖 source.businessRegistrationId（来源执照）
    return { ...source, ...common };
  }
  return {
    ...common,
    companyName: '—',
    registrationNo: '—',
  };
}

function buildNameChangePendingListItem(pending, items) {
  if (!isBrPendingCode(statusCodeOf(pending))) {
    return null;
  }
  const submitted = pick(pending, 'submittedDate', 'SubmittedDate', null);
  return mergeNameChangeCard(pending, items, {
    statusCode: BR_STATUS.PENDING_MANUAL,
    updatedDate: submitted,
    isNameChangePending: true,
  });
}

function buildNameChangeRejectedListItem(pending, items) {
  const code = statusCodeOf(pending);
  if (code !== BR_STATUS.REJECTED) {
    return null;
  }
  const submitted = pick(pending, 'submittedDate', 'SubmittedDate', null);
  const updated = pick(pending, 'updatedDate', 'UpdatedDate', submitted);
  const rejectReason = pick(pending, 'rejectReason', 'RejectReason', '');
  return mergeNameChangeCard(pending, items, {
    statusCode: BR_STATUS.REJECTED,
    updatedDate: updated,
    isNameChangePending: false,
    rejectReason,
  });
}

/** 已失效更名卡：被更新 Submit Time 的已通过更名覆盖（Status=I，非驳回） */
function buildNameChangeInvalidatedListItem(pending, items) {
  const code = statusCodeOf(pending);
  if (code !== BR_STATUS.INVALID) {
    return null;
  }
  const submitted = pick(pending, 'submittedDate', 'SubmittedDate', null);
  const updated = pick(pending, 'updatedDate', 'UpdatedDate', submitted);
  return mergeNameChangeCard(pending, items, {
    statusCode: BR_STATUS.INVALID,
    updatedDate: updated,
    isNameChangePending: false,
  });
}

/**
 * 已通过更名：来源照 I + previous 显示名；结果新照挂「修改公司名称」红字并永久保留
 * （resultApprovalId=更名本单；resultBusinessRegistrationId=提交时已建主档）。
 */
export function composeBrListWithNameChanges(items, nameChanges = []) {
  const list = filterLinkedBrListItems(items).map((item) => ({ ...item }));
  const changes = normalizePendingList(nameChanges)
    .slice()
    .sort((a, b) => (Number(approvalIdOf(a)) || 0) - (Number(approvalIdOf(b)) || 0));

  changes.forEach((nc) => {
    if (statusCodeOf(nc) !== BR_STATUS.APPROVED) {
      return;
    }
    const prevEn = pickTrim(nc, 'previousDisplayNameEn', 'PreviousDisplayNameEn');
    const prevZh = pickTrim(nc, 'previousDisplayNameZh', 'PreviousDisplayNameZh');
    const sourceApprovalId = pick(nc, 'sourceApprovalId', 'SourceApprovalId', null);
    const sourceBrId = pick(nc, 'sourceBusinessRegistrationId', 'SourceBusinessRegistrationId', null);
    const resultApprovalId = pick(nc, 'resultApprovalId', 'ResultApprovalId', null);
    const resultBrId = pick(nc, 'resultBusinessRegistrationId', 'ResultBusinessRegistrationId', null);
    const nameChangeApprovalId = pick(nc, 'approvalId', 'ApprovalId', null);

    list.forEach((item, idx) => {
      const approvalId = approvalIdOf(item);
      const brId = brIdOf(item);
      const itemStatus = statusCodeOf(item);

      const isResult = idsEqual(approvalId, resultApprovalId) || idsEqual(brId, resultBrId);
      if (isResult) {
        list[idx] = {
          ...item,
          isNameChangeResultCard: true,
          isSupersededByNameChange: false,
          requestKind: BR_REQUEST_KIND.NAME_CHANGE_RESULT,
          nameChangeApprovalId,
          previousDisplayNameEn: prevEn,
          previousDisplayNameZh: prevZh,
        };
        return;
      }

      const isSource = idsEqual(approvalId, sourceApprovalId) || idsEqual(brId, sourceBrId);
      if (!isSource || itemStatus !== BR_STATUS.INVALID) {
        return;
      }

      const keepResultKind = !!(item.isNameChangeResultCard || item.IsNameChangeResultCard);
      list[idx] = {
        ...item,
        displayNameEn: prevEn || pick(item, 'displayNameEn', 'DisplayNameEn', ''),
        displayNameZh: prevZh || pick(item, 'displayNameZh', 'DisplayNameZh', ''),
        isSupersededByNameChange: true,
        isNameChangeResultCard: keepResultKind,
        ...(keepResultKind ? { requestKind: BR_REQUEST_KIND.NAME_CHANGE_RESULT } : {}),
      };
    });
  });

  return list;
}

/** 列表分区：真实 BR + 更名申请卡（待审 / 驳回 / 已失效） */
export function buildBusinessDocumentsListSections(items, nameChanges, t, options = {}) {
  const changes = normalizePendingList(nameChanges);
  const composed = annotateBrItemsWithPendingNameChangeSource(
    composeBrListWithNameChanges(items, changes),
    changes,
  );
  const sections = groupBrListItems(composed, t, options);

  const extraItems = [
    ...changes.map((row) => buildNameChangePendingListItem(row, items)).filter(Boolean),
    ...changes.map((row) => buildNameChangeRejectedListItem(row, items)).filter(Boolean),
    ...changes.map((row) => buildNameChangeInvalidatedListItem(row, items)).filter(Boolean),
  ];
  if (!extraItems.length) {
    return sections;
  }

  const nameChangeRequestApprovalIds = buildNameChangeRequestApprovalIdSet(extraItems);
  const withoutDuplicateRequests = (sectionItems) => withoutDuplicateNameChangeRequestRows(
    sectionItems,
    nameChangeRequestApprovalIds,
  );

  const othersIndex = sections.findIndex((section) => section.key === 'others');
  if (othersIndex >= 0) {
    sections[othersIndex] = {
      ...sections[othersIndex],
      items: sortBrListItemsByLastUpdated([
        ...extraItems,
        ...withoutDuplicateRequests(sections[othersIndex].items),
      ]),
    };
  } else {
    sections.push({
      key: 'others',
      label: t('lbl_EMPBE_BR_List_Section_Others'),
      items: sortBrListItemsByLastUpdated(extraItems),
    });
  }

  const effectiveIndex = sections.findIndex((section) => section.key === 'effective');
  if (effectiveIndex >= 0) {
    sections[effectiveIndex] = {
      ...sections[effectiveIndex],
      items: withoutDuplicateRequests(sections[effectiveIndex].items),
    };
  }

  return sections;
}
